import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Server,
    Globe,
    FolderTree,
    Users,
    User,
    Monitor,
    ShieldAlert,
    UserX,
    ScanSearch,
    RefreshCw,
    Network,
    Shield,
    Lock,
    ArrowLeft,
    Folder,
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';

import type { ADDomain, ADOU, ADUser, ADComputer, ADGroup } from './adTypes';

import ADTreeView from './ADTreeView';
import ADTopologyFlow from './ADTopologyFlow';
import ADGPOView from './ADGPOView';
import ADPasswordPolicyView from './ADPasswordPolicyView';

export default function ActiveDirectoryMainV2() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const level = useClientLevel();
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        navigate('/asset');
    }, [level, navigate]);

    // Session storage internal IDs
    const info = JSON.parse(sessionStorage.getItem('internal_ids') || '{}');
    const asset_id = info.asset_id;
    const name = info.name || 'Active Directory Topology';
    const agent_id = info.agent_id;

    // States
    const [loading, setLoading] = useState(true);
    const [domain, setDomain] = useState<ADDomain | null>(null);
    const [ous, setOus] = useState<ADOU[]>([]);
    const [users, setUsers] = useState<ADUser[]>([]);
    const [computers, setComputers] = useState<ADComputer[]>([]);
    const [groups, setGroups] = useState<ADGroup[]>([]);

    const activeTab = searchParams.get('tab') || 'tree';

    // Initiate AD Scan task
    const handleInitiateADScan = async () => {
        try {
            const response = await baseapi.post('/agents/agent-task', {
                agent_id: agent_id,
                job_type: 'ad_scan',
            });
            if (response.data?.status) {
                showSuccess(response.data.message || 'AD Scan initiated successfully');
            } else {
                showError(response.data?.message || 'Failed to initiate AD Scan');
            }
        } catch (error: any) {
            showError(error.message || 'Scan error');
        }
    };

    // Fetch API Data selectively based on activeTab
    const loadTabData = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        try {
            const queryObj: any = { is_deleted: false };
            if (asset_id) queryObj['asset_id'] = asset_id;
            const queryStr = encodeURIComponent(JSON.stringify(queryObj));

            const apiCalls: Promise<any>[] = [];
            const callKeys: string[] = [];

            // 1. Domain summary is fetched if not present or on refresh
            if (!domain || forceRefresh) {
                apiCalls.push(baseapi.get(`/ad_topology/domain?asset_id=${asset_id || ''}&client_id=${level?.id || ''}`));
                callKeys.push('domain');
            }

            // 2. Fetch Users, Computers, Groups upfront for Topology Graph tab
            if (activeTab === 'graph') {
                if (users.length === 0 || forceRefresh) {
                    apiCalls.push(baseapi.get(`/ad_users?query=${queryStr}&limit=1000`));
                    callKeys.push('users');
                }
                if (computers.length === 0 || forceRefresh) {
                    apiCalls.push(baseapi.get(`/ad_computers?query=${queryStr}&limit=500`));
                    callKeys.push('computers');
                }
                if (groups.length === 0 || forceRefresh) {
                    apiCalls.push(baseapi.get(`/ad_groups?query=${queryStr}&limit=1000`));
                    callKeys.push('groups');
                }
            }

            if (apiCalls.length > 0) {
                const results = await Promise.allSettled(apiCalls);
                results.forEach((res, idx) => {
                    const key = callKeys[idx];
                    if (res.status === 'fulfilled' && res.value?.data) {
                        const val = res.value.data;
                        if (key === 'domain' && val.domain) {
                            setDomain(val.domain);
                            if (val.ous) setOus(val.ous);
                        } else if (key === 'users' && val.data) {
                            setUsers(val.data);
                        } else if (key === 'computers' && val.data) {
                            setComputers(val.data);
                        } else if (key === 'groups' && val.data) {
                            setGroups(val.data);
                        }
                    }
                });
            }
        } catch (err: any) {
            console.warn('AD Topology API error:', err.message);
        } finally {
            setLoading(false);
        }
    }, [asset_id, level?.id, activeTab, domain, users.length, computers.length, groups.length]);

    useEffect(() => {
        loadTabData();
    }, [activeTab]); // Triggers strictly when activeTab changes!

    // Computed Stats Header values
    const stats = useMemo(() => {
        const adminCount = users.filter(u => u.domain_admin || u.enterprise_admin).length;
        const disabledCount = users.filter(u => u.account_disabled).length;

        return {
            domains: domain ? 1 : 0,
            ous: domain?.ou_count ?? ous.length,
            users: domain?.user_count ?? users.length,
            computers: domain?.computer_count ?? computers.length,
            groups: domain?.group_count ?? groups.length,
            admins: domain?.domain_admin_count ?? adminCount,
            disabled: domain?.disabled_user_count ?? disabledCount,
        };
    }, [domain, ous, users, computers, groups]);

    return (
        <div className="flex flex-col w-full bg-slate-50 dark:bg-gray-900 text-slate-800 dark:text-gray-200 min-h-[calc(100vh-4rem)] overflow-hidden font-sans">
            {/* Top Action Header */}
            <div className="flex justify-between items-center border-b border-slate-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 px-5 py-2.5 sticky top-0 z-30 backdrop-blur-md">
                <div className="flex items-center gap-2">
                    <div className="bg-orange-600 p-1.5 rounded border border-orange-500 shrink-0">
                        <Server className="text-white w-4 h-4" />
                    </div>
                    <div>
                        <h1 className="text-base font-bold text-slate-900 dark:text-white tracking-wide leading-tight">{name}</h1>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={() => loadTabData(true)}
                        disabled={loading}
                        className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 text-slate-700 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white rounded-lg transition-all"
                        title="Refresh AD Data"
                    >
                        <RefreshCw size={15} className={loading ? 'animate-spin text-orange-500' : ''} />
                    </button>

                    <button
                        className="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 flex items-center gap-1.5 text-white px-3.5 py-2 rounded-lg transition-all text-xs font-semibold shadow-sm"
                        onClick={handleInitiateADScan}
                    >
                        <ScanSearch className="w-4 h-4" />
                        <span>Initiate Scan</span>
                    </button>

                    <button
                        onClick={() => navigate('/asset/dashboard')}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 flex items-center gap-1.5 text-white rounded-lg transition-all text-xs font-semibold"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Back</span>
                    </button>
                </div>
            </div>

            {/* Status Strip & Tabs Bar */}
            <div className="bg-white/80 dark:bg-gray-900/80 border-b border-slate-200 dark:border-gray-800 px-5 py-2.5 flex items-center justify-between gap-4 overflow-x-auto custom-scroll shrink-0">
                <div className="flex items-center gap-2.5 flex-nowrap">
                    {/* Domain Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <Globe size={15} className="text-orange-500" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Domain:</span>
                        <span className="text-orange-600 dark:text-orange-500 font-bold text-sm">{stats.domains}</span>
                    </div>

                    {/* OUs Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <Folder size={15} className="text-amber-500 dark:text-amber-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">OUs:</span>
                        <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">{stats.ous}</span>
                    </div>

                    {/* Users Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <User size={15} className="text-emerald-500 dark:text-emerald-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Users:</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">{stats.users}</span>
                    </div>

                    {/* Computers Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <Monitor size={15} className="text-purple-500 dark:text-purple-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Computers:</span>
                        <span className="text-purple-600 dark:text-purple-400 font-bold text-sm">{stats.computers}</span>
                    </div>

                    {/* Groups Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <Users size={15} className="text-indigo-500 dark:text-indigo-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Groups:</span>
                        <span className="text-indigo-600 dark:text-indigo-400 font-bold text-sm">{stats.groups}</span>
                    </div>

                    {/* Admins Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <ShieldAlert size={15} className="text-red-500 dark:text-red-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Admins:</span>
                        <span className="text-red-600 dark:text-red-400 font-bold text-sm">{stats.admins}</span>
                    </div>

                    {/* Disabled Pill */}
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100/90 hover:bg-slate-100 dark:bg-gray-800/90 dark:hover:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-lg text-xs transition-colors shrink-0">
                        <UserX size={15} className="text-slate-400 dark:text-gray-400" />
                        <span className="text-slate-600 dark:text-gray-200 text-xs font-medium uppercase tracking-wide">Disabled:</span>
                        <span className="text-slate-800 dark:text-gray-300 font-bold text-sm">{stats.disabled}</span>
                    </div>
                </div>

                {/* Segmented Tab Switcher */}
                <div className="flex items-center bg-slate-100 dark:bg-gray-800/90 p-1.5 rounded-xl border border-slate-200 dark:border-gray-700/60 gap-1 shrink-0">
                    <button
                        onClick={() => setSearchParams({ tab: 'tree' })}
                        className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${activeTab === 'tree' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200 hover:bg-slate-200/60 dark:hover:bg-gray-700/40'
                            }`}
                    >
                        <FolderTree size={15} />
                        <span>Tree View</span>
                    </button>
                    <button
                        onClick={() => setSearchParams({ tab: 'graph' })}
                        className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${activeTab === 'graph' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200 hover:bg-slate-200/60 dark:hover:bg-gray-700/40'
                            }`}
                    >
                        <Network size={15} />
                        <span>Topology</span>
                    </button>

                    <button
                        onClick={() => setSearchParams({ tab: 'gpos' })}
                        className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${activeTab === 'gpos' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200 hover:bg-slate-200/60 dark:hover:bg-gray-700/40'
                            }`}
                    >
                        <Shield size={15} />
                        <span>GPOs</span>
                    </button>

                    <button
                        onClick={() => setSearchParams({ tab: 'policies' })}
                        className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${activeTab === 'policies' ? 'bg-orange-600 text-white shadow-md' : 'text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-gray-200 hover:bg-slate-200/60 dark:hover:bg-gray-700/40'
                            }`}
                    >
                        <Lock size={15} />
                        <span>Password Policy</span>
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-4 overflow-y-auto custom-scroll">
                {activeTab === 'graph' && (
                    <ADTopologyFlow
                        domain={domain}
                        ous={ous}
                        users={users}
                        computers={computers}
                        groups={groups}
                        loading={loading}
                    />
                )}

                {activeTab === 'tree' && (
                    <ADTreeView
                        domain={domain}
                        ous={ous}
                        users={users}
                        computers={computers}
                        groups={groups}
                    />
                )}

                {activeTab === 'gpos' && <ADGPOView asset_id={asset_id} />}

                {activeTab === 'policies' && <ADPasswordPolicyView asset_id={asset_id} />}
            </div>
        </div>
    );
}
